// =============================================================================
// BP-07-03: ResourceGrid Component
// =============================================================================

import { Grid, List, Loader2 } from 'lucide-react';
import { ResourceCard } from './ResourceCard';
import type { Resource } from '@/features/resources/types/resourcesTypes';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface ColumnConfig {
  default?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

interface ResourceGridProps {
  resources: Resource[];
  viewMode?: 'grid' | 'list';
  isLoading?: boolean;
  showCategory?: boolean;
  showDiagnosis?: boolean;
  emptyMessage?: string;
  className?: string;
  columns?: ColumnConfig; // Custom column configuration
  onSaveToggle?: (resourceId: string, isSaved: boolean) => void;
}

interface ViewModeToggleProps {
  viewMode: 'grid' | 'list';
  onChange: (mode: 'grid' | 'list') => void;
}

// -----------------------------------------------------------------------------
// ViewModeToggle Component
// -----------------------------------------------------------------------------

export function ViewModeToggle({ viewMode, onChange }: ViewModeToggleProps) {
  return (
    <div className="flex items-center gap-1 p-1 bg-cream-dark rounded-lg">
      <button
        onClick={() => onChange('grid')}
        className={cn(
          'p-2 rounded-md transition-colors',
          viewMode === 'grid'
            ? 'bg-white text-sage shadow-soft'
            : 'text-warm-gray hover:text-navy'
        )}
        aria-label="Grid view"
      >
        <Grid className="w-4 h-4" />
      </button>
      <button
        onClick={() => onChange('list')}
        className={cn(
          'p-2 rounded-md transition-colors',
          viewMode === 'list'
            ? 'bg-white text-sage shadow-soft'
            : 'text-warm-gray hover:text-navy'
        )}
        aria-label="List view"
      >
        <List className="w-4 h-4" />
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Loading Skeleton
// -----------------------------------------------------------------------------

function ResourceCardSkeleton({ variant = 'grid' }: { variant?: 'grid' | 'list' }) {
  if (variant === 'list') {
    return (
      <div className="flex gap-4 p-4 rounded-xl bg-white border border-warm-gray/10 animate-pulse">
        <div className="w-32 h-24 rounded-lg bg-cream-dark" />
        <div className="flex-1 space-y-3">
          <div className="h-3 w-20 bg-cream-dark rounded" />
          <div className="h-5 w-3/4 bg-cream-dark rounded" />
          <div className="h-3 w-full bg-cream-dark rounded" />
          <div className="h-3 w-24 bg-cream-dark rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-white border border-warm-gray/10 overflow-hidden animate-pulse">
      <div className="aspect-[16/9] bg-cream-dark" />
      <div className="p-4 space-y-3">
        <div className="h-3 w-20 bg-cream-dark rounded" />
        <div className="h-5 w-full bg-cream-dark rounded" />
        <div className="h-3 w-3/4 bg-cream-dark rounded" />
        <div className="h-3 w-1/2 bg-cream-dark rounded" />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Empty State
// -----------------------------------------------------------------------------

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="w-16 h-16 rounded-full bg-cream-dark flex items-center justify-center mb-4">
        <List className="w-8 h-8 text-warm-gray" />
      </div>
      <p className="text-warm-gray">{message}</p>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ResourceGrid Component
// -----------------------------------------------------------------------------

export function ResourceGrid({
  resources,
  viewMode = 'grid',
  isLoading = false,
  showCategory = true,
  showDiagnosis = false,
  emptyMessage = 'No resources found',
  className,
  columns,
  onSaveToggle,
}: ResourceGridProps) {
  // Filter out any undefined/null resources
  const validResources = resources?.filter((r): r is Resource => r != null) ?? [];

  // Build grid classes based on columns config or use defaults
  const getGridClasses = () => {
    if (columns) {
      const classes = ['grid gap-6'];
      classes.push(`grid-cols-${columns.default || 1}`);
      if (columns.sm) classes.push(`sm:grid-cols-${columns.sm}`);
      if (columns.md) classes.push(`md:grid-cols-${columns.md}`);
      if (columns.lg) classes.push(`lg:grid-cols-${columns.lg}`);
      if (columns.xl) classes.push(`xl:grid-cols-${columns.xl}`);
      return classes.join(' ');
    }
    return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6';
  };

  const gridClasses = getGridClasses();

  // Loading state
  if (isLoading) {
    return (
      <div
        className={cn(
          viewMode === 'grid' ? gridClasses : 'flex flex-col gap-4',
          className
        )}
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <ResourceCardSkeleton key={i} variant={viewMode} />
        ))}
      </div>
    );
  }

  // Empty state
  if (!validResources.length) {
    return <EmptyState message={emptyMessage} />;
  }

  // Grid view
  if (viewMode === 'grid') {
    return (
      <div className={cn(gridClasses, className)}>
        {validResources.map((resource) => (
          <ResourceCard
            key={resource.id}
            resource={resource}
            variant="default"
            showCategory={showCategory}
            showDiagnosis={showDiagnosis}
            onSaveToggle={onSaveToggle}
          />
        ))}
      </div>
    );
  }

  // List view
  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {validResources.map((resource) => (
        <ResourceCard
          key={resource.id}
          resource={resource}
          variant="horizontal"
          showCategory={showCategory}
          showDiagnosis={showDiagnosis}
          onSaveToggle={onSaveToggle}
        />
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// ResourceGridWithLoadMore (Fixed)
// -----------------------------------------------------------------------------

interface EmptyAction {
  label: string;
  onClick?: () => void;
  href?: string;
}

interface ResourceGridWithLoadMoreProps extends ResourceGridProps {
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  fetchNextPage?: () => void; // Alias for onLoadMore
  emptyAction?: EmptyAction;
}

export function ResourceGridWithLoadMore({
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  fetchNextPage,
  emptyAction,
  emptyMessage = 'No resources found',
  ...props
}: ResourceGridWithLoadMoreProps) {
  const validResources = props.resources?.filter((r): r is Resource => r != null) ?? [];
  const handleLoadMore = onLoadMore || fetchNextPage;

  // Show empty state with action
  if (!props.isLoading && validResources.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-cream-dark flex items-center justify-center mb-4">
          <List className="w-8 h-8 text-warm-gray" />
        </div>
        <p className="text-warm-gray mb-4">{emptyMessage}</p>
        {emptyAction && (
          emptyAction.href ? (
            <a
              href={emptyAction.href}
              className="px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
            >
              {emptyAction.label}
            </a>
          ) : (
            <button
              onClick={emptyAction.onClick}
              className="px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
            >
              {emptyAction.label}
            </button>
          )
        )}
      </div>
    );
  }

  return (
    <div>
      <ResourceGrid {...props} emptyMessage={emptyMessage} />

      {(hasNextPage || isFetchingNextPage) && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={handleLoadMore}
            disabled={isFetchingNextPage}
            className={cn(
              'px-6 py-3 rounded-lg font-medium transition-all',
              'bg-sage text-white hover:bg-sage-dark',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isFetchingNextPage ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </span>
            ) : (
              'Load More'
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default ResourceGrid;

